import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Radio,
} from '@mui/material';
import { API_BASE } from '../../utils/api';

const COVER_BASE = `${API_BASE}/static/images/book-covers`;

function thumbUrl(draft) {
  const versions = draft.coverVersions || [];
  const active = versions.find(v => v.id === draft.activeCoverVersionId) || versions[0];
  // A normalized URL is servable; a raw coverImage key is not, so prefer the URL.
  // Per-version presigned URLs don't exist yet, so a version-only key still falls
  // through to the key branch below and will not resolve — see RV ticket.
  const img = draft.cover_url || active?.coverImage || draft.coverImage || '';
  if (!img) return null;
  if (img.startsWith('http')) return img;
  if (img.startsWith('/')) return `${API_BASE}${img}`;
  return `${COVER_BASE}/${img}`;
}

export default function LinkModal({ open, onClose, mode, currentLinkedId, onConfirm }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(currentLinkedId || null);

  const accessToken = localStorage.getItem('rv_access_token');

  const isCoverMode = mode === 'cover';
  const title = isCoverMode ? 'Link to Cover' : 'Link to Manuscript';
  const emptyMsg = isCoverMode
    ? "You don't have any cover-only drafts yet."
    : "You don't have any manuscript drafts yet.";

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(currentLinkedId || null);
    setLoading(true);

    const type = isCoverMode ? 'cover_only' : 'manuscript';
    fetch(`${API_BASE}/api/drafts?authored=true&type=${type}`, {
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, mode, currentLinkedId, accessToken, isCoverMode]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter(d => (d.title || '').toLowerCase().includes(term));
  }, [search, items]);

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by title…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
            {search ? 'No results match your search.' : emptyMsg}
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {/* Unlink option when something is already linked */}
            {currentLinkedId && (
              <Box
                onClick={() => setSelected(null)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: selected === null ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Radio checked={selected === null} size="small" />
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Remove link
                </Typography>
              </Box>
            )}

            {filtered.map(draft => {
              const thumb = thumbUrl(draft);
              const isSelected = selected === (draft._id || draft.id);
              return (
                <Box
                  key={draft._id || draft.id}
                  onClick={() => setSelected(draft._id || draft.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Radio checked={isSelected} size="small" onChange={() => setSelected(draft._id || draft.id)} />
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt={draft.title}
                      sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }}
                    />
                  ) : (
                    <Box sx={{ width: 36, height: 36, bgcolor: '#413B8D', borderRadius: 0.5, flexShrink: 0 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {draft.title || 'Untitled'}
                    </Typography>
                    {draft.genre && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {draft.genre}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selected === currentLinkedId}
          sx={{ bgcolor: '#413B8D', '&:hover': { bgcolor: '#322f6e' } }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
